import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/ui/button";
import { Kbd } from "@/ui/kbd";
import { ATALHO_DA_PALETA, ICONES, Icone, iniciais, railDe } from "../visual.jsx";
import { useAcoes, useEstado, useJanela, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Contador, MenuDeAcoes } from "./comuns.jsx";
import { densidadeDoTrilho } from "./trilho.js";
import Escritorio from "./Escritorio.jsx";

/*
 * Quantas conversas esperam resposta — pelo contador do BANCO.
 *
 * Somar `nao_lidas` da `mesa` somava a PÁGINA: com os 528 casos da planilha, a
 * pastilha do sino dizia menos trabalho do que existe, e justamente para quem
 * tem mais. `contadores` vem da mesma consulta da mesa (`contadoresDaMesa` em
 * `src/leads.ts`) e conta conversas com mensagem não lida, não mensagens.
 */
const naoLidasDe = (mesa, contadores) => contadores?.["Não lidos"] ?? mesa.reduce((a, c) => a + (c.nao_lidas || 0), 0);

/**
 * O número no canto de cada ícone.
 *
 * Eram duas linhas iguais dizendo `item.chave === "chats" ? naoLidas : 0` — e
 * com isso o trilho tinha UMA pastilha, a de mensagem sem resposta. Prazo
 * vencido é a outra coisa que não pode esperar alguém abrir a tela para
 * descobrir: quem não passa por Tarefas não sabe que passou do prazo.
 *
 * Só duas. Pastilha em tudo é pastilha em nada — e "atrasados" da agenda são as
 * MESMAS tarefas contadas de novo, que somaria o mesmo alarme duas vezes.
 */
const pastilhaDe = (chave, { naoLidas, tarefasAtrasadas }) =>
  chave === "chats" ? naoLidas : chave === "tarefas" ? tarefasAtrasadas : 0;

/*
 * O realce de um botão SOBRE A TINTA.
 *
 * `Button variant="ghost"` do shadcn dá o foco visível, o `disabled` e o afundar
 * de 1px no clique — mas clareia no hover com `bg-muted`, que é papel. O trilho
 * é a superfície escura: aqui o realce é o creme translúcido.
 */
const SOBRE_A_TINTA = "hover:bg-sidebar-accent hover:text-sidebar-foreground dark:hover:bg-sidebar-accent";

/* O contador no canto do ícone é o `Contador` da casa (20, "99+" em todo
   lugar — era "9+" aqui e o número cru na aba do celular). O contorno é da COR
   DA COLUNA, não do cartão: ele é o vão que separa o número do ícone por baixo. */
const SOBRE_A_COLUNA = "shadow-[0_0_0_2px_var(--color-sidebar)]";

/* ------------------------------------------------------------ modo aberto */

/*
 * Trilho fixado é preferência da MÁQUINA, não endereço.
 *
 * Não vira query como o resto do recorte (`?painel=`, `?filtro=`) porque não é
 * recorte de uma tela: vale para as onze, e um link de atendimento mandado por
 * mensagem não deve reabrir o trilho do jeito de quem mandou. Fica no
 * `localStorage`, como a configuração de gravação — e some sem drama numa aba
 * em modo restrito.
 */
const CHAVE_ABERTO = "galera.trilho.aberto";
export const lerTrilhoAberto = () => {
  try {
    return localStorage.getItem(CHAVE_ABERTO) === "1";
  } catch {
    return false;
  }
};
export const salvarTrilhoAberto = (aberto) => {
  try {
    localStorage.setItem(CHAVE_ABERTO, aberto ? "1" : "0");
  } catch {
    /* aba em modo restrito: vale para esta sessão e pronto */
  }
};

/* FIXAR o trilho come 246px da largura da mesa. Abaixo de 1180px a mesa já
   esconde a lista de conversas (`Casca.jsx`); fixar ali seria tirar espaço de
   quem já não tem — a preferência fica guardada e volta a valer na tela grande.
   Espiar com o ponteiro continua valendo em qualquer largura, porque espiar
   FLUTUA por cima e não tira pixel de ninguém. */
export const LARGURA_MINIMA_PARA_ABRIR = 1180;

/* As duas larguras do trilho. Exportadas porque a casca reserva a coluna e
   quem flutua no canto de baixo (o balão do sino) se encosta nela. */
export const TRILHO_FECHADO = 56;
export const TRILHO_ABERTO = 246;

/* Quanto o ponteiro precisa DEMORAR para abrir, e quanto pode sair antes de
   fechar. Abrir na hora faz o trilho saltar quando o mouse só atravessa a
   coluna a caminho da conversa; fechar na hora pune quem escorrega 3px para
   fora ao mirar um item. */
const ATRASO_ABRIR = 130;
const ATRASO_FECHAR = 220;

/* ---------------------------------------------------------------- as peças */

/*
 * A linha do trilho: 38px de altura, ícone à esquerda, nome quando há largura.
 *
 * `[&_svg]:size-…!` não é enfeite: a classe base do `Button` encolhe para 16px
 * todo `<svg>` que não traga classe de tamanho própria, e o ícone da casa
 * (`Icone`, Hugeicons) dá o tamanho por ATRIBUTO — sem isto a coluna inteira
 * diminuía. O `!` é necessário porque o seletor de lá (`svg:not([class*=size-])`)
 * pesa mais que este.
 *
 * Fundo transparente no item LIGADO de propósito: quem pinta o item aberto é a
 * pastilha que desliza por baixo (`marca`), e duas pinturas no mesmo lugar
 * fariam a que desliza chegar num alvo já pintado — o movimento sumiria.
 *
 * `mx-auto` fechado: quando a altura aperta, a linha encolhe abaixo dos 40px
 * da coluna e ficava colada à esquerda, enquanto a pastilha (que centra pela
 * conta) e a marca do topo ficavam no meio — os ícones saíam do eixo.
 */
const LINHA = (ligado, aberto) =>
  `group relative z-[1] h-[var(--trilho-item)] flex-none rounded-[11px] bg-transparent transition-[color,background-color,transform] duration-[var(--t-toque)] [&_svg]:size-[18px]! ${
    aberto ? "w-full justify-start gap-2.5 px-2.5" : "mx-auto w-[var(--trilho-item)] justify-center p-0"
  } ${
    ligado
      ? /* `hover:text-creme` explícito: a variante `ghost` do registro escreve
           `hover:text-accent-foreground`, que é a TINTA — sobre a pastilha creme
           do lugar aberto o nome escurecia até sumir assim que o ponteiro
           encostava. Só apagar o fundo do hover não basta; a cor da letra é
           outra declaração. */
        "text-creme hover:bg-transparent hover:text-creme dark:hover:bg-transparent dark:hover:text-creme"
      : `text-sidebar-foreground/55 hover:text-sidebar-foreground ${SOBRE_A_TINTA}`
  }`;

/* O nome do lugar. Fechado ele não some do DOM — encolhe: montar e desmontar
   o texto faz a palavra piscar nas duas pontas da animação, e o que se quer é
   ela ENTRANDO junto com a coluna. */
const ROTULO = (aberto) =>
  `min-w-0 truncate text-left text-corpo font-semibold transition-[opacity,transform] duration-[var(--t-curta)] ease-entra ${
    aberto ? "flex-1 translate-x-0 opacity-100" : "w-0 -translate-x-1 opacity-0"
  }`;

/* A escadinha: cada nome entra 14ms depois do de cima, e todos somem juntos.
   Escalonar na saída daria a impressão de o trilho travando ao fechar. */
const degrau = (aberto, i) => ({ transitionDelay: aberto ? `${40 + i * 14}ms` : "0ms" });

/** A contagem do item: selo no canto do ícone quando fechado, coluna quando aberto. */
function Conta({ valor, aberto }) {
  if (!valor) return null;
  return aberto ? (
    /* Aberto a contagem é coluna da linha, não selo em cima do ícone: sobre um
       rótulo escrito ela taparia a palavra. */
    <Contador n={valor} />
  ) : (
    /* Selo BAIXO (16 e não 20, a letra continua 11) e pendurado PARA FORA do
       quadrado, à direita. Com "99+" o selo de 20 encostado no canto tapava o
       ícone inteiro — dois itens vizinhos com 99+ viravam dois selos
       empilhados sem ícone nenhum entre eles. Assim ele só toca a quina do
       ícone, usa os 8px de sobra da coluna (56) e não alcança o item de cima
       nem com a linha encolhida a 30px. */
    <Contador n={valor} classe={`absolute -top-1 -right-2 h-4 min-w-4 px-1 ${SOBRE_A_COLUNA}`} />
  );
}

/* -------------------------------------------------------------- o trilho */

/**
 * O trilho da lateral: a casca inteira da mesa, numa coluna só.
 *
 * Aqui mora tudo que vale para as onze telas — a marca, o escritório desta aba,
 * a porta da paleta, os onze lugares, o sino e quem está logado. Era uma faixa
 * de 38px atravessada em cima MAIS uma coluna de 48px na lateral: duas cascas
 * para a mesma função, e a faixa comia a altura da conversa em todas as telas.
 *
 * Fechado são 56px de ícone. O ponteiro chega perto e ele ABRE SOZINHO em
 * 246px, FLUTUANDO por cima da mesa — abrir empurrando reflui a conversa
 * inteira a cada vez que o mouse passa, que é o motivo de trilho que abre no
 * hover ter má fama. Quem quer o nome sempre à vista fixa no alfinete, e aí sim
 * a coluna passa a ocupar lugar de verdade.
 *
 * Sem balão de dica em item nenhum daqui: o balão da casa abre em 120ms e a
 * coluna em 130 — a dica nasceria e o nome escrito apareceria em cima dela, no
 * mesmo décimo de segundo. Abrir a coluna É a dica; quem não vê o ícone
 * continua com o `aria-label`, que nunca dependeu de hover.
 *
 * O teclado tem o mesmo direito do ponteiro: focar qualquer coisa aqui dentro
 * abre a coluna, e ela só fecha quando o foco sai. Esc fecha o que o ponteiro
 * abriu, sem mexer no que está fixado.
 */
export function Trilho({ fixado, podeFixar, aoFixar, aoAbrirPaleta }) {
  const { mesa, contadores, avisos, usuario, tarefasAtrasadas } = useEstado();
  const alturaJanela = useJanela((j) => j.altura);
  const { sair } = useAcoes();
  const nav = useNavegacao();
  const { vista, avisosAbertos } = nav;
  const naoLidas = naoLidasDe(mesa, contadores);
  /* Só os lugares que esta pessoa alcança — e os grupos já sem os vazios. */
  const { itens: railVisivel, grupos, rodape } = railDe(usePode());
  /* Sem `useMemo`: são três somas e uma divisão sobre listas de dez itens, e
     `grupos` nasce novo a cada render — memoizar sobre ele não guardava nada e
     ainda fazia o compilador do React desistir de otimizar o componente. */
  const densidade = densidadeDoTrilho(
    alturaJanela,
    grupos.reduce((n, g) => n + g.itens.length, 0),
    grupos.length,
    /* Zero: o que está no pé virou item de menu, não linha. */
    0,
  );

  /* Espiar pelo ponteiro e espiar pelo teclado são estados SEPARADOS: o Esc
     fecha o primeiro e não pode fechar o segundo, senão quem navega tabulando
     perde os nomes no meio do caminho. */
  const [porPonteiro, setPorPonteiro] = useState(false);
  const [porFoco, setPorFoco] = useState(false);
  /* O balão do sino também segura a coluna: ele nasce ENCOSTADO nela, e o
     ponteiro que sai do sino para dentro do balão sai da coluna — ela fechava
     por baixo do balão, que ficava pendurado a 190px do nada. */
  const aberto = fixado || avisosAbertos || porPonteiro || porFoco;
  const flutuando = aberto && !fixado;

  const relogio = useRef(0);
  const raiz = useRef(null);
  useEffect(() => () => clearTimeout(relogio.current), []);

  /* O destino já agendado, para o gatilho poder ser o MOVIMENTO sem virar
     "abre só quando o mouse para": `pointermove` dispara dezenas de vezes ao
     atravessar a coluna, e reagendar a cada um adiaria a abertura para sempre. */
  const destino = useRef(null);
  const marcar = useCallback((valor, atraso) => {
    if (destino.current === valor) return;
    clearTimeout(relogio.current);
    destino.current = valor;
    relogio.current = setTimeout(() => {
      destino.current = null;
      setPorPonteiro(valor);
    }, atraso);
  }, []);

  /* Só o mouse espia. No dedo não existe "chegar perto": o `pointerenter` de um
     toque vem junto com o clique, e a coluna abriria por cima do que a pessoa
     acabou de tocar. */
  /*
   * Abre no MOVIMENTO do ponteiro, não no `pointerenter`.
   *
   * O navegador dispara evento de fronteira quando a página muda EMBAIXO de um
   * cursor parado — e o canto superior esquerdo é onde o mouse costuma estar
   * depois de clicar em "Entrar" ou de trocar de aba. Resultado: a mesa abria
   * com a coluna escancarada por cima da lista, tapando o nome de todo mundo, e
   * o primeiro clique numa conversa caía na coluna. `pointermove` só existe
   * quando alguém mexeu o mouse de verdade, que é a intenção que a coluna
   * espia. Achado da auditoria de 22/08 — a jornada ponta a ponta ficou cinco
   * minutos tentando clicar num nome coberto.
   */
  const entrou = (e) => e.pointerType === "mouse" && marcar(true, ATRASO_ABRIR);
  const saiu = (e) => {
    if (e.pointerType !== "mouse") return;
    /* Menu aberto (a conta, o escritório) segura a coluna: o painel nasce ancorado
       no gatilho, e fechar por baixo dele deixaria o menu pendurado no vazio. */
    if (raiz.current?.querySelector("[data-popup-open]")) return;
    marcar(false, ATRASO_FECHAR);
  };

  /*
   * A pastilha que desliza entre os lugares.
   *
   * Ela é UMA, e anda: onze fundos acendendo e apagando dizem "mudou", um único
   * bloco indo de um item ao outro diz DE ONDE PARA ONDE — que é a informação
   * que a pessoa usa para não se perder na coluna.
   *
   * O topo é medido (o grupo tem título aberto e um fio fechado, então a altura
   * acima do item muda); a largura e o recuo são CALCULADOS. Medir a largura
   * durante a transição leria o valor do meio da animação e a pastilha nasceria
   * do tamanho errado.
   */
  const itens = useRef(new Map());
  const lista = useRef(null);
  const [topo, setTopo] = useState(null);
  useLayoutEffect(() => {
    const caixa = lista.current;
    if (!caixa) return;
    const medir = () => {
      const el = itens.current.get(vista);
      setTopo(el ? el.offsetTop : null);
    };
    medir();
    /* Remedir enquanto a coluna se abre, e não só quando `aberto` muda: o
       título do grupo entra no lugar do fio e empurra os itens para baixo, e
       ler uma vez no começo da animação daria o topo do estado ANTIGO — a
       pastilha chegaria no lugar errado e corrigiria com um pulo. */
    const olho = new ResizeObserver(medir);
    olho.observe(caixa);
    return () => olho.disconnect();
  }, [vista]);

  return (
    /* A coluna RESERVADA tem a largura do estado fixado; o painel por dentro é
       que cresce. É o que faz espiar não empurrar a mesa. */
    <div className="relative z-[20] flex-none" style={{ width: fixado ? TRILHO_ABERTO : TRILHO_FECHADO }}>
      {/* `nav` e `aria-current`: são onze botões iguais, e a vista aberta se dizia
          só por cor de fundo — quem usa leitor de tela ouvia onze botões e não
          sabia em qual dos onze lugares estava, nem tinha marco para pular até
          aqui. */}
      <nav
        ref={raiz}
        aria-label="Áreas da mesa"
        onPointerMove={entrou}
        onPointerLeave={saiu}
        onFocus={() => setPorFoco(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setPorFoco(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && porPonteiro) {
            clearTimeout(relogio.current);
            setPorPonteiro(false);
          }
        }}
        className={`absolute inset-y-0 left-0 flex flex-col overflow-hidden bg-sidebar text-sidebar-foreground transition-[width,box-shadow] duration-[var(--t-media)] ease-entra ${
          flutuando ? "shadow-[0_24px_70px_-14px_color-mix(in_oklab,_var(--color-foreground)_60%,_transparent)]" : "shadow-none"
        }`}
        style={{
          width: aberto ? TRILHO_ABERTO : TRILHO_FECHADO,
          /* Uma medida só, lida por classe (`h-[var(--trilho-item)]`) em todo
             lugar do trilho: item, paleta, sino, conta e a pastilha que desliza
             por baixo. Duas contas diferentes fariam a pastilha parar num alvo
             de outro tamanho. */
          "--trilho-item": `${densidade.item}px`,
          "--trilho-grupo": `${densidade.titulo}px`,
        }}
      >
        {/* ---------------------------------------------------------- a marca */}
        <div className="flex h-[52px] flex-none items-center gap-2 px-[9px]">
          {/* As letras FGA são vazadas, então na tinta vale a versão branca — a
              navy sumiria. */}
          <span className="grid size-[38px] flex-none place-items-center rounded-[11px] bg-creme/[.07]">
            <img src="/fga-mark-branca.png" alt="Galera Advocacia" className="block h-[17px] w-auto" />
          </span>
          {/* Ao lado da marca: a marca diz de quem é o sistema, isto diz em QUAL
              escritório esta aba está escrevendo — e trocar de lado numa aba muda
              o banco de destino das outras. */}
          <div
            className={`min-w-0 flex-1 transition-opacity duration-[var(--t-curta)] ${aberto ? "opacity-100" : "pointer-events-none opacity-0"}`}
          >
            <Escritorio variante="barra" />
          </div>
          {/*
            O SINO, ao lado do alfinete — e não escondido no menu da conta.
            
            Ele é a única coisa da coluna que muda sozinha: prazo curto, SLA
            estourando, o que a IA mexeu. Escondê-lo atrás de um menu economizava
            38px de altura e cobrava um clique a mais justamente no que se olha
            no começo do dia. Aqui ele não custa linha nenhuma — divide os 52px
            que a marca já ocupava — e o número fica à vista.

            Só com a coluna ABERTA: fechada, estes 26px disputariam a faixa com a
            marca. Ali o sinal é o número no retrato da conta, no pé, e a porta é
            o item do menu.
          */}
          {aberto && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => nav.ajustar({ avisos: !avisosAbertos })}
              data-dica="Notificações"
              data-dica-lado="baixo"
              aria-label="Notificações"
              aria-expanded={avisosAbertos}
              className={`relative size-[26px] flex-none rounded-[8px] [&_svg]:size-[16px]! ${SOBRE_A_TINTA} ${
                avisosAbertos ? "bg-sidebar-accent text-sidebar-foreground" : "text-sidebar-foreground/55"
              }`}
            >
              <Icone de={ICONES.sino} tamanho={16} />
              {/* Número, não bolinha: "tem algo" e "tem dezoito prazos" pedem
                  reações diferentes. */}
              <Contador n={avisos.length} tom="ambar" classe={`absolute -top-2 -right-2 ${SOBRE_A_COLUNA}`} />
            </Button>
          )}
          {/*
            O alfinete: manda a coluna PARAR aberta.
            Só existe onde fixar é permitido — botão que não faz nada é pior que
            botão que não existe —, e só aparece com a coluna aberta, porque
            fechada ele disputaria os 38px com o ícone do lugar.
          */}
          {podeFixar && (
            <Button
              variant="ghost"
              size="icon"
              onClick={aoFixar}
              data-dica={fixado ? "Soltar o trilho (volta a abrir no ponteiro)" : "Fixar o trilho aberto"}
              data-dica-lado="baixo"
              aria-label={fixado ? "Soltar o trilho" : "Fixar o trilho aberto"}
              aria-pressed={fixado}
              /* O alfinete DEITA quando está solto e CRAVA quando fixa: o giro
                 é o que faz o estado ser lido de longe, sem depender de notar
                 que o fundo mudou de tom. */
              className={`size-[26px] flex-none rounded-[8px] transition-[opacity,background-color,color] duration-[var(--t-curta)] [&_svg]:size-[15px]! [&_svg]:transition-transform [&_svg]:duration-[var(--t-toque)] [&_svg]:ease-entra ${SOBRE_A_TINTA} ${
                aberto ? "opacity-100" : "pointer-events-none opacity-0"
              } ${
                fixado
                  ? "bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent [&_svg]:rotate-0"
                  : "text-sidebar-foreground/55 [&_svg]:-rotate-45 [&_svg]:hover:-rotate-12"
              }`}
            >
              <Icone de={ICONES.fixar} tamanho={15} />
            </Button>
          )}
        </div>

        {/*
          A porta da paleta.
          Não é navegação: é o campo onde se digita PARA ONDE ir — a área, o
          cliente, o caso de ontem. O atalho vem escrito porque paleta que só se
          abre pelo mouse é campo de busca com passos extras.
        */}
        <div className="flex-none px-2 pb-1.5">
          <button
            type="button"
            onClick={aoAbrirPaleta}
            aria-label={`Ir para uma área ou um cliente (${ATALHO_DA_PALETA})`}
            className={`flex h-[var(--trilho-item)] items-center rounded-[11px] border border-transparent bg-creme/[.06] text-sidebar-foreground/55 transition-[width,background-color,color] duration-[var(--t-media)] ease-entra outline-none hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-creme/40 ${
              aberto ? "w-full gap-2.5 px-2.5" : "mx-auto w-[var(--trilho-item)] justify-center"
            }`}
          >
            <Icone de={ICONES.busca} tamanho={16} />
            <span className={ROTULO(aberto)} style={degrau(aberto, 0)}>
              Ir para… área, cliente, caso
            </span>
            <Kbd
              className={`h-[17px] flex-none bg-creme/10 px-1.5 text-selo text-sidebar-foreground/70 transition-opacity duration-[var(--t-curta)] ${
                aberto ? "opacity-100" : "hidden opacity-0"
              }`}
            >
              {ATALHO_DA_PALETA}
            </Kbd>
          </button>
        </div>

        {/*
          O SINO, os AJUSTES e a AJUDA saíram das linhas próprias.

          Cada um custava 38px de coluna — 114px só de "coisas do sistema" numa
          superfície que precisa caber inteira em qualquer altura de tela, e que
          existe para responder "onde eu trabalho". Os três viraram itens do menu
          da conta, no pé: é lá que já moravam as decisões sobre a ferramenta (e o
          "Sair"). O que não podia sumir era o SINAL de que há aviso esperando —
          ele continua à vista, como número no retrato da conta.
        */}

        {/* ------------------------------------------------------- os lugares */}
        {/* As vistas rolam. Onze linhas mais os três títulos passam de 440px, e num
            notebook de 720 a coluna não teria onde pôr a décima segunda — e o
            defeito seria invisível: item fora da tela, sem barra e sem sinal.
            `min-h-0` porque item de coluna flex não encolhe abaixo do conteúdo sem
            isso, e a barra é escondida de propósito — uma barra clara de 8px na
            única superfície escura da mesa é mancha, não informação. */}
        <div
          ref={lista}
          className="relative flex min-h-0 flex-1 flex-col gap-0.5 overflow-x-hidden overflow-y-auto px-2 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {/* A pastilha do lugar aberto, por baixo dos botões. Rola junto com a
              lista porque mora DENTRO dela — presa por fora, ela ficaria parada
              enquanto o item que ela marca sobe. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-0 left-0 h-[var(--trilho-item)] rounded-[11px] bg-creme/[.13] ring-1 ring-creme/[.09] transition-[transform,width,opacity] duration-[var(--t-media)] ease-entra ring-inset"
            style={{
              width: aberto ? TRILHO_ABERTO - 16 : densidade.item,
              /* O recuo do item: 8px de respiro da coluna, mais o 1px que sobra
                 quando o botão de 38 é centrado nos 56 fechados. */
              transform: `translate3d(${aberto ? 8 : (TRILHO_FECHADO - densidade.item) / 2}px, ${topo ?? 0}px, 0)`,
              opacity: topo === null ? 0 : 1,
            }}
          >
            {/* O fiozinho creme na borda: aberto ele dá a direção do lugar; fechado
                a pastilha é um quadrado em volta do ícone e o fio viraria sujeira. */}
            <span
              className={`absolute top-[10px] left-0 h-[18px] w-[3px] rounded-r-full bg-creme transition-opacity duration-[var(--t-curta)] ${
                aberto ? "opacity-100" : "opacity-0"
              }`}
            />
          </span>

          {grupos.map(({ grupo, titulo, itens: doGrupo }, i) => (
            <div key={grupo} className="flex flex-col gap-0.5">
              {/*
                A cabeça do grupo tem ALTURA FIXA nos dois estados.
                Fechado ela é um fio; aberto, o nome do grupo — e as duas coisas
                se trocam por opacidade, dentro da mesma caixa de 28px. Trocar
                fio por texto mudava a altura, e a coluna inteira descia dois
                dedos toda vez que ela abria: o ícone que a mão foi buscar não
                estava mais onde a mão mirou.
              */}
              {/* Some inteira quando a altura aperta — some o NOME, não o grupo: o
                  fio continua embaixo, que é como a coluna fechada já separa. */}
              <div className={`relative flex-none ${densidade.titulo ? "h-[var(--trilho-grupo)]" : "h-[9px]"}`}>
                {densidade.titulo ? (
                  <span
                    className={`absolute inset-x-0 bottom-[5px] truncate px-2.5 text-selo font-bold tracking-[.1em] whitespace-nowrap text-sidebar-foreground/40 uppercase transition-opacity duration-[var(--t-curta)] ${
                      aberto ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {titulo}
                  </span>
                ) : null}
                {/* O fio só entre grupos: acima do primeiro não há o que separar.
                    Sem o título ele é o ÚNICO separador, e aí vale aberta também
                    — senão os três grupos viram uma lista corrida de onze itens. */}
                {i > 0 && (
                  <span
                    className={`absolute left-1/2 h-px -translate-x-1/2 bg-sidebar-foreground/15 transition-opacity duration-[var(--t-curta)] ${
                      densidade.titulo
                        ? `bottom-[13px] w-[20px] ${aberto ? "opacity-0" : "opacity-100"}`
                        : `bottom-[4px] opacity-100 ${aberto ? "w-[calc(100%-20px)]" : "w-[20px]"}`
                    }`}
                  />
                )}
              </div>
              {doGrupo.map((item) => {
                const ligado = vista === item.chave;
                const badge = pastilhaDe(item.chave, { naoLidas, tarefasAtrasadas });
                return (
                  <Button
                    key={item.chave}
                    ref={(el) => {
                      if (el) itens.current.set(item.chave, el);
                      else itens.current.delete(item.chave);
                    }}
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      nav.irParaVista(item.chave);
                      /* Escolheu: a coluna some na hora. Deixar aberta esperando o
                         ponteiro sair cobre justamente a tela que a pessoa pediu. */
                      clearTimeout(relogio.current);
                      setPorPonteiro(false);
                    }}
                    aria-label={item.rotulo}
                    aria-current={ligado ? "page" : undefined}
                    className={LINHA(ligado, aberto)}
                  >
                    <Icone de={item.icone} tamanho={18} />
                    <span className={ROTULO(aberto)} style={degrau(aberto, railVisivel.indexOf(item) + 2)}>
                      {item.rotulo}
                    </span>
                    <Conta valor={badge} aberto={aberto} />
                  </Button>
                );
              })}
            </div>
          ))}
        </div>

        {/* --------------------------------------------------------- o rodapé */}
        {/* Só a conta, separada dos lugares por um fio: ela não é lugar. "Sair"
            saiu do avatar — que era um botão que encerrava a sessão à primeira
            batida de dedo — e virou item do menu. */}
        <div className="flex flex-none flex-col gap-0.5 border-t border-sidebar-border px-2 pt-2 pb-2.5">
          {/* O que é do pé, e não do dia: a Ajuda (consulta rara) e a conta. Cada
              uma continua uma LINHA do trilho — mesma altura, mesmo alvo —, e a
              Ajuda saiu da lista de lugares porque uma coluna que precisa caber
              inteira paga 38px por linha. */}
          <MenuDeAcoes
            classe={`${LINHA(false, aberto)} inline-flex items-center outline-none focus-visible:ring-2 focus-visible:ring-creme/40 data-[popup-open]:bg-sidebar-accent`}
            estilo={() => ""}
            ancora="right end"
            largura="200px"
            legenda={usuario?.nome ?? "Minha conta"}
            rotulo={`Minha conta (${usuario?.nome ?? ""})`}
            /* Notificações NÃO entra aqui: o sino mora no alto, ao lado do
               alfinete, e ter as duas portas fazia o mesmo aviso ser contado em
               dois lugares da mesma coluna. O menu da conta é o que se toca uma
               vez por semana — ajustes, manual, sair. */
            itens={[
              ...rodape.map((item) => ({
                chave: item.chave,
                rotulo: item.rotulo,
                icone: item.icone,
                aoTocar: () => nav.irParaVista(item.chave),
              })),
              { chave: "sair", rotulo: "Sair da mesa", icone: ICONES.sair, aoTocar: sair },
            ]}
          >
            <span className="flex size-[26px] flex-none items-center justify-center overflow-hidden rounded-controle bg-verde-fundo text-selo font-bold text-sobre-terra">
              {usuario?.avatar ? (
                <img src={usuario.avatar} alt="" className="block size-full object-cover" />
              ) : (
                iniciais(usuario?.nome)
              )}
            </span>
            <span className={ROTULO(aberto)} style={degrau(aberto, railVisivel.length + 3)}>
              {usuario?.nome ?? "Minha conta"}
            </span>
            {/* A seta SAI do DOM quando a coluna fecha, e não fica invisível: com
                `flex:none` ela continuava ocupando 13px dentro dos 38 do botão,
                e empurrava o retrato para fora do centro — o único item do
                trilho que não ficava alinhado com os outros. */}
            {aberto && <Icone de={ICONES.baixo} tamanho={13} estilo={{ flex: "none", opacity: 0.5 }} />}
          </MenuDeAcoes>
        </div>
      </nav>
    </div>
  );
}

/* ------------------------------------------------- a faixa, só no celular */

/**
 * No telefone não há coluna: a marca, o escritório, a busca e o sino ficam numa
 * faixa de 38px em cima, e navegar é a barra de abas embaixo.
 *
 * No computador esta faixa não existe mais — tudo que ela carregava mora no
 * trilho, que é a casca inteira. Aqui ela sobrevive porque 56px de coluna numa
 * tela de 390 é um quinto da largura, e o dedo não tem hover para abrir nada.
 */
export function BarraSuperior({ aoAbrirPaleta }) {
  const { avisos, usuario } = useEstado();
  const { sair } = useAcoes();
  const nav = useNavegacao();
  const { vista } = nav;

  return (
    <header className="z-[10] flex h-[var(--barra-da-mesa)] flex-none items-center gap-1.5 bg-sidebar px-2 pt-[env(safe-area-inset-top)] text-sidebar-foreground">
      <img src="/fga-mark-branca.png" alt="Galera Advocacia" className="block h-[19px] w-auto flex-none" />
      <div className="min-w-0 max-w-[150px]">
        <Escritorio variante="barra" />
      </div>

      <div className="min-w-[6px] flex-1" />

      <button
        type="button"
        onClick={aoAbrirPaleta}
        aria-label={`Ir para uma área ou um cliente (${ATALHO_DA_PALETA})`}
        /* 28px de altura, e não 26: alvo de toque abaixo de 28 reprova em
           `layout.spec.ts` — com razão, é o dedo que não acerta. */
        className={`alvo flex h-[28px] min-w-[30px] items-center justify-center rounded-lg bg-sidebar-accent/50 px-2.5 text-sidebar-foreground/55 transition-colors outline-none hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-creme/40`}
      >
        <Icone de={ICONES.busca} tamanho={14} />
      </button>

      <Button
        variant="ghost"
        size="icon"
        /* No telefone o sino vai direto para a TELA: um balão de 330px pendurado
           numa faixa de 390 é a tela inteira com moldura. */
        onClick={() => nav.ir("/notificacoes")}
        aria-label="Notificações"
        aria-current={vista === "avisos" ? "page" : undefined}
        className={`alvo size-[28px] flex-none rounded-[9px] text-sidebar-foreground [&_svg]:size-[17px]! ${SOBRE_A_TINTA} ${
          vista === "avisos" ? "bg-sidebar-accent" : ""
        }`}
      >
        <Icone de={ICONES.sino} tamanho={17} />
        <Contador n={avisos.length} tom="ambar" classe={`absolute -top-1.5 -right-1.5 ${SOBRE_A_COLUNA}`} />
      </Button>

      <MenuDeAcoes
        classe={`alvo flex flex-none items-center gap-1.5 rounded-[9px] px-1 py-0.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-creme/40 ${SOBRE_A_TINTA} data-[popup-open]:bg-sidebar-accent`}
        estilo={() => ""}
        ancora="bottom end"
        largura="min(260px, 90vw)"
        legenda={usuario?.nome ?? "Minha conta"}
        rotulo={`Minha conta (${usuario?.nome ?? ""})`}
        titulo="Minha conta"
        itens={[{ chave: "sair", rotulo: "Sair da mesa", icone: ICONES.sair, aoTocar: sair }]}
      >
        <span className="flex size-[24px] flex-none items-center justify-center overflow-hidden rounded-controle bg-verde-fundo text-selo font-bold text-sobre-terra">
          {usuario?.avatar ? (
            <img src={usuario.avatar} alt="" className="block size-full object-cover" />
          ) : (
            iniciais(usuario?.nome)
          )}
        </span>
      </MenuDeAcoes>
    </header>
  );
}

/* ---------------------------------------------------- barra do celular */

/* No celular a aba tem rótulo, e rótulo pede LARGURA. */
const NO_TRABALHO = (i) => i.grupo === "trabalho";

/* `flex items-center justify-center` e o anel de foco vêm escritos porque a aba
   "Mais" é o gatilho do menu (um `<button>` cru do Base UI), e não o `Button` do
   shadcn: sem isso ela nasce sem coluna, sem centro e sem foco visível ao lado
   de quatro irmãs iguais. */
/* `min-h-[48px]`: é a medida de alvo de toque que todo aplicativo usa, e a barra
   é a única navegação que existe no telefone — errar a aba aqui custa uma ida e
   volta inteira. `active:scale-[.92]` porque toque sem resposta imediata parece
   travamento: o dedo tapa o próprio alvo, e a única confirmação que sobra é o
   elemento encolher sob ele. `touch-action:manipulation` mata o atraso de 300ms
   que o navegador guarda esperando um duplo-toque de zoom. */
const ABA_DO_CELULAR = (ligado) =>
  `relative flex h-auto min-h-[48px] min-w-0 flex-1 touch-manipulation flex-col items-center justify-center gap-[3px] rounded-xl px-0.5 pt-[7px] pb-[5px] transition-[background-color,color,transform] duration-[var(--t-toque)] outline-none select-none active:scale-[.92] focus-visible:ring-2 focus-visible:ring-creme/40 [&_svg]:size-[19px]! ${SOBRE_A_TINTA} ${
    ligado ? "bg-sidebar-accent text-sidebar-foreground" : "text-sidebar-foreground/60"
  }`;

/** A navegação no celular, embaixo: quatro abas do dia e "Mais". */
export function BarraInferior() {
  const { mesa, contadores, tarefasAtrasadas } = useEstado();
  const nav = useNavegacao();
  const { vista } = nav;
  const naoLidas = naoLidasDe(mesa, contadores);
  /* As mesmas duas regras do trilho: só o que a pessoa alcança. No celular isto
     pesa mais — cinco abas de 35px numa tela de 412, e uma delas levando a uma
     tela que responde 403 é um quinto da barra gasto num beco. */
  const { itens: railVisivel } = railDe(usePode());
  const naBarra = railVisivel.filter(NO_TRABALHO);
  const atrasDoMais = railVisivel.filter((i) => !NO_TRABALHO(i));
  const noMais = atrasDoMais.find((i) => i.chave === vista);

  return (
    <nav
      aria-label="Áreas da mesa"
      /* `pb` somando `safe-area-inset-bottom`: no iPhone e no Android com
         navegação por gestos a faixa de baixo é área do SISTEMA — sem isso a
         fileira de abas nasce debaixo do risco do "home", e tocar em "Tarefas"
         manda a pessoa para a tela inicial do telefone. */
      className="z-[8] flex flex-none bg-sidebar px-1.5 pt-1.5 pb-[calc(9px+env(safe-area-inset-bottom))] text-sidebar-foreground"
    >
      {/*
        Quatro abas e "Mais", não as onze.
        As onze davam 35px por aba numa tela de 412 — e "Contatos" a 9px mede 46,
        então o rótulo transbordava a própria aba e encostava no vizinho:
        "ContatosFluxos" numa palavra só. Quem fica é o trabalho do dia; o resto
        (acervo e escritório) é ida ocasional e cabe atrás de um menu.
      */}
      <div className="flex min-w-0 flex-1 gap-0.5">
        {naBarra.map((item) => {
          const ligado = vista === item.chave;
          const badge = pastilhaDe(item.chave, { naoLidas, tarefasAtrasadas });
          return (
            <Button
              key={item.chave}
              variant="ghost"
              onClick={() => nav.irParaVista(item.chave)}
              aria-current={ligado ? "page" : undefined}
              /* O nome INTEIRO: a aba escreve "Atend." por falta de largura, e o
                 leitor de tela (e a seta da demonstração, que procura o rótulo
                 do trilho) precisa do nome do lugar, não da abreviação. */
              aria-label={item.rotulo}
              className={ABA_DO_CELULAR(ligado)}
            >
              <Icone de={item.icone} tamanho={19} />
              {/* `nowrap`: rótulo que quebra em duas linhas cresce a barra inteira. */}
              <span className="text-selo font-semibold tracking-[.02em] whitespace-nowrap">{item.curto}</span>
              <Contador n={badge} classe={`absolute top-[3px] right-3.5 ${SOBRE_A_COLUNA}`} />
            </Button>
          );
        })}
        {/* A aba "Mais" DIZ onde se está: em Ajustes, o rótulo é "Ajustes" e a
            aba fica acesa — senão a pessoa toca nas quatro para descobrir por
            que nenhuma está marcada. */}
        <MenuDeAcoes
          classe={ABA_DO_CELULAR(Boolean(noMais))}
          estilo={() => ""}
          ancora="top end"
          largura="210px"
          altura="min(60vh,340px)"
          legenda="OUTRAS ÁREAS"
          rotulo={noMais ? `Mais áreas — ${noMais.rotulo}` : "Mais áreas"}
          itens={atrasDoMais.map((item) => ({
            chave: item.chave,
            rotulo: item.rotulo,
            icone: item.icone,
            titulo: item.chave === vista ? "Você está aqui" : `Ir para ${item.rotulo}`,
            aoTocar: () => nav.irParaVista(item.chave),
          }))}
        >
          <Icone de={noMais ? noMais.icone : ICONES.mais} tamanho={19} />
          <span className="text-selo font-semibold tracking-[.02em] whitespace-nowrap">{noMais ? noMais.curto : "Mais"}</span>
        </MenuDeAcoes>
      </div>
    </nav>
  );
}
